import React, { useEffect, useState, lazy, Suspense, memo, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Button,
  IconButton,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  useTheme,
  alpha,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Step,
  Stepper,
  StepLabel,
  DialogContentText,
  Card,
  CardContent,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Tooltip,
  Skeleton,
  Tabs,
  Tab,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Description as DescriptionIcon,
  CloudUpload as CloudUploadIcon,
  InsertDriveFile as InsertDriveFileIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
  AutoAwesome as AutoAwesomeIcon,
  Rule as RuleIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  Visibility as VisibilityIcon,
  Assignment as AssignmentIcon,
  Person as PersonIcon,
  CalendarToday as CalendarTodayIcon,
  Business as BusinessIcon,
  Refresh as RefreshIcon,
  AccountBalance as AccountBalanceIcon,
  Event as EventIcon,
  EventNote as EventNoteIcon,
  Notes as NotesIcon,
  LocationCity as LocationCityIcon,
  Close as CloseIcon,
  Inventory as InventoryIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { Project, Permit, Jurisdiction, User, Document as DocumentType, ComplianceCheck, BuildingCode, DocumentComplianceReport } from '../types';
import { projectAPI, permitAPI, jurisdictionAPI, documentComplianceAPI } from '../apis';
import { useDropzone } from 'react-dropzone';
import ProjectOverview from '../components/ProjectTabs/ProjectOverview';
import ProjectComplianceChecks from '../components/ProjectTabs/ProjectComplianceChecks';
import ProjectDocuments from '../components/ProjectTabs/ProjectDocuments';
import ProjectHistory from '../components/ProjectTabs/ProjectHistory';
import ProjectMaterials from '../components/ProjectTabs/ProjectMaterials';
import DocumentDialog from '../components/ProjectTabs/DocumentDialog';
import CreatePermitDialog from '../components/ProjectTabs/CreatePermitDialog';
import ProjectLabor from '../components/ProjectTabs/ProjectLabor';

const PERMIT_TYPES = [
  'Building Permit',
  'Electrical Permit',
  'Plumbing Permit',
  'Mechanical Permit',
  'Demolition Permit',
  'Grading Permit',
  'Site Development Permit',
  'Other'
];

// Helper function to get status icon
const getStatusIcon = (status: string) => {
  switch (status) {
    case 'approved':
      return <CheckCircleIcon color="success" />;
    case 'rejected':
      return <CancelIcon color="error" />;
    case 'in_review':
      return <WarningIcon color="warning" />;
    default:
      return <CircularProgress size={20} />;
  }
};

// Helper function to get status chip
const getStatusChip = (status: string) => {
  const statusConfig = {
    approved: { color: 'success' as const, icon: <CheckCircleIcon /> },
    rejected: { color: 'error' as const, icon: <CancelIcon /> },
    in_review: { color: 'warning' as const, icon: <WarningIcon /> },
    compliant: { color: 'success' as const, icon: <CheckCircleIcon /> },
    non_compliant: { color: 'error' as const, icon: <CancelIcon /> },
    pending: { color: 'warning' as const, icon: <WarningIcon /> }
  };
  const config = statusConfig[status.toLowerCase() as keyof typeof statusConfig] || statusConfig.pending;
  return (
    <IconButton
      size="small"
      sx={{
        color: `${config.color}.main`,
        padding: '4px',
        '&:hover': {
          backgroundColor: (theme) => alpha(theme.palette[config.color].main, 0.08)
        }
      }}
    >
      {config.icon}
    </IconButton>
  );
};

const ProjectDetailsSkeleton = () => {
  const theme = useTheme();
  
  return (
    <Suspense fallback={<CircularProgress />}>
      <Container maxWidth="xl">
        <Box
          component="main"
          sx={{
            pt: '64px',
            minHeight: '100vh',
            bgcolor: 'background.default',
            pb: 4,
          }}
        >
          <Container maxWidth="lg">
              {/* Header with back button and title */}
            <Box sx={{ mb: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Skeleton 
                  variant="circular" 
                  width={40} 
                  height={40} 
                  sx={{ 
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                  }}
                />
                  <Skeleton variant="text" width={300} height={40} />
                </Box>

              {/* Tabs Skeleton */}
              <Box sx={{ mt: 1, borderBottom: 1, borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  {[...Array(4)].map((_, index) => (
                    <Skeleton
                      key={index}
                      variant="rectangular"
                      width={120}
                      height={40}
                      sx={{ 
                        borderRadius: 1,
                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                      }}
                    />
                  ))}
                </Box>
                </Box>
              </Box>

            <Box sx={{ mt: 3 }}>
              <Grid container spacing={3}>
                <Grid container item xs={12} lg={6} spacing={3}>
                  {/* Project Details Card */}
                  <Grid item xs={12}>
                    <Card sx={{ borderRadius: '16px' }}>
                      <CardHeader 
                        title={<Skeleton variant="text" width={200} />}
                      />
                      <CardContent>
                    <Grid container spacing={2}>
                          {[...Array(6)].map((_, index) => (
                        <Grid item xs={12} key={index}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Skeleton variant="circular" width={24} height={24} />
                            <Box sx={{ flex: 1 }}>
                              <Skeleton variant="text" width="30%" height={20} />
                              <Skeleton variant="text" width="60%" height={24} />
                            </Box>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                      </CardContent>
                    </Card>
                </Grid>
                </Grid>

                {/* Right Column - Compliance Checks and Recent Documents */}
                <Grid item xs={12} lg={6}>
                  {/* Compliance Checks Card */}
                  <Card sx={{ borderRadius: '16px' }}>
                    <CardHeader 
                      title={<Skeleton variant="text" width={200} />}
                      action={
                        <Skeleton variant="rectangular" width={100} height={36} sx={{ borderRadius: '8px' }} />
                      }
                    />
                    <CardContent>
                      <Table>
                        <TableHead>
                          <TableRow>
                            {['Document', 'Type', 'Status', 'Actions'].map((_, index) => (
                              <TableCell key={index}>
                                <Skeleton variant="text" width="80%" />
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {[...Array(3)].map((_, rowIndex) => (
                            <TableRow key={rowIndex}>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Skeleton variant="circular" width={20} height={20} />
                                  <Skeleton variant="text" width={120} />
                        </Box>
                              </TableCell>
                              <TableCell>
                                <Skeleton variant="text" width={80} />
                              </TableCell>
                              <TableCell>
                                <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: '8px' }} />
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Skeleton variant="circular" width={30} height={30} />
                                  <Skeleton variant="circular" width={30} height={30} />
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                  {/* Recent Documents Card Skeleton below Compliance Checks */}
                  <Card sx={{ borderRadius: '16px', mt: 3 }}>
                    <CardHeader 
                      title={<Skeleton variant="text" width={200} />}
                      action={
                        <Skeleton variant="rectangular" width={100} height={36} sx={{ borderRadius: '8px' }} />
                      }
                    />
                    <CardContent>
                      {[...Array(3)].map((_, index) => (
                        <Box 
                          key={index}
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            p: 1.5,
                            mb: 1,
                            gap: 2,
                            bgcolor: alpha(theme.palette.primary.main, 0.04),
                            borderRadius: '8px'
                          }}
                        >
                          <Skeleton variant="circular" width={20} height={20} />
                          <Box sx={{ flex: 1 }}>
                            <Skeleton variant="text" width="70%" height={20} />
                            <Skeleton variant="text" width="40%" height={16} />
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Skeleton variant="circular" width={30} height={30} />
                            <Skeleton variant="circular" width={30} height={30} />
                          </Box>
                        </Box>
                      ))}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          </Container>
        </Box>
      </Container>
    </Suspense>
  );
};

// Add this component before the main ProjectDetails component
const DocumentListDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  documents: Array<DocumentType & { permitType: string }>;
  onDocumentClick: (document: DocumentType) => void;
  onReupload: (document: DocumentType) => void;
  onDelete: (document: DocumentType) => void;
  error: string | null;
  isSubmitting: boolean;
}> = ({ open, onClose, documents, onDocumentClick, onReupload, onDelete, error, isSubmitting }) => {
  const theme = useTheme();
  
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">All Documents ({documents.length})</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <List sx={{ width: '100%' }}>
          {documents.map((document) => (
            <ListItem
              key={document.documentID}
              disablePadding
              sx={{
                mb: 1,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden',
              }}
            >
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                width: '100%',
                p: 1.5,
                gap: 2
              }}>
                <InsertDriveFileIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                <Box 
                  onClick={() => onDocumentClick(document)}
                  sx={{ 
                    flex: 1,
                    minWidth: 0,
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.04),
                    },
                  }}
                >
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {document.name}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{
                      display: 'block',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {document.permitType} • Version {document.version}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="Reupload">
                    <IconButton 
                      size="small"
                      onClick={() => onReupload(document)}
                      disabled={isSubmitting}
                      sx={{ 
                        color: theme.palette.primary.main,
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.04),
                        },
                      }}
                    >
                      {isSubmitting ? (
                        <CircularProgress size={20} />
                      ) : (
                        <CloudUploadIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton 
                      size="small"
                      onClick={() => onDelete(document)}
                      disabled={isSubmitting}
                      sx={{ 
                        color: theme.palette.error.main,
                        '&:hover': {
                          bgcolor: alpha(theme.palette.error.main, 0.04),
                        },
                      }}
                    >
                      {isSubmitting ? (
                        <CircularProgress size={20} />
                      ) : (
                        <DeleteIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </ListItem>
          ))}
        </List>
      </DialogContent>
    </Dialog>
  );
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`project-tabpanel-${index}`}
      aria-labelledby={`project-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ProjectDetails: React.FC = memo(() => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const [project, setProject] = useState<Project | null>(null);
  const [jurisdiction, setJurisdiction] = useState<Jurisdiction | null>(null);
  const [owner, setOwner] = useState<User | null>(null);
  const [permits, setPermits] = useState<Permit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDocument, setSelectedDocument] = useState<DocumentType | null>(null);
  const [isDocumentDialogOpen, setIsDocumentDialogOpen] = useState(false);
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Permit creation dialog state
  const [isCreatePermitDialogOpen, setIsCreatePermitDialogOpen] = useState(false);
  const [isPermitDetailsDialogOpen, setIsPermitDetailsDialogOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [permitData, setPermitData] = useState({
    permitType: '',
    estimatedTimeline: '',
  });
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png']
    },
    onDrop: (acceptedFiles) => {
      setSelectedFiles(acceptedFiles);
      setUploadError(null);
    },
    onDropRejected: () => {
      setUploadError('Invalid file type. Please upload PDF, DOC, DOCX, JPG, or PNG files.');
    }
  });

  const handleDialogClose = () => {
    setIsCreatePermitDialogOpen(false);
    setActiveStep(0);
    setPermitData({
      permitType: '',
      estimatedTimeline: '',
    });
    setSelectedFiles([]);
    setUploadError(null);
  };

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPermitData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    if (!project) return;

    try {
      setError(null);
      setIsSubmitting(true);
      const response = await permitAPI.createPermit(
        {
          ...permitData,
          projectID: project.projectID,
        },
        selectedFiles
      );

      if (response.status === 'success' && response.data) {
        setPermits(prev => [...prev, response.data]);
        handleDialogClose();
      } else {
        throw new Error(response.message || 'Failed to create permit');
      }
    } catch (error) {
      console.error('Error creating permit:', error);
      setError('Failed to create permit. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDocumentClick = async (document: DocumentType) => {
    try {
      setSelectedDocument(document);
      setIsDocumentDialogOpen(true);
      
      // Get signed URL for the document
      const response = await permitAPI.getDocument(document.permitID, document.documentID);
      
      if (response.status === 'success' && response.data) {
        setDocumentUrl(response.data.downloadUrl);
      } else {
        console.error('Error fetching document URL:', response.message);
        setDocumentUrl(null);
      }
    } catch (error) {
      console.error('Error fetching document:', error);
      setDocumentUrl(null);
    }
  };

  const handleViewComplianceReport = (documentId: string) => {
    navigate(`/document-compliance/${documentId}`);
  };

  const handleDocumentDialogClose = () => {
    setSelectedDocument(null);
    setDocumentUrl(null);
    setIsDocumentDialogOpen(false);
  };

  const [selectedPermit, setSelectedPermit] = useState<Permit | null>(null);

  const handlePermitClick = (permit: Permit) => {
    setSelectedPermit(permit);
    setIsPermitDetailsDialogOpen(true);
  };

  const handlePermitDetailsDialogClose = () => {
    setSelectedPermit(null);
    setIsPermitDetailsDialogOpen(false);
  };

  const handleCreatePermitDialogClose = () => {
    setIsCreatePermitDialogOpen(false);
    setActiveStep(0);
    setPermitData({
      permitType: '',
      estimatedTimeline: '',
    });
    setSelectedFiles([]);
    setUploadError(null);
  };

  const [complianceChecks, setComplianceChecks] = useState<ComplianceCheck[]>([]);
  const [buildingCodes, setBuildingCodes] = useState<Record<string, BuildingCode>>({});
  const [loadingCompliance, setLoadingCompliance] = useState(false);
  const [complianceError, setComplianceError] = useState<string | null>(null);
  const [isRunningCheck, setIsRunningCheck] = useState(false);
  const [processingStep, setProcessingStep] = useState(0);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  const processingSteps = [
    {
      icon: <SearchIcon />,
      text: "Scanning Documents",
      subtext: "Analyzing project documentation..."
    },
    {
      icon: <AutoAwesomeIcon />,
      text: "AI Analysis",
      subtext: "Processing with advanced AI models..."
    },
    {
      icon: <RuleIcon />,
      text: "Code Verification",
      subtext: "Checking against building codes..."
    },
    {
      icon: <AssignmentTurnedInIcon />,
      text: "Generating Report",
      subtext: "Preparing compliance findings..."
    }
  ];

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isRunningCheck) {
      interval = setInterval(() => {
        setProcessingStep((prev) => {
          if (prev < processingSteps.length - 1) {
            return prev + 1;
          }
          return 0;
        });
      }, 2000); // Change step every 2 seconds
    } else {
      setProcessingStep(0);
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isRunningCheck]);

  const fetchProjectDetails = useCallback(async () => {
    if (!projectId) return;

    try {
      setLoading(true);
      setError(null);
      setComplianceError(null);

      // Parallelize API calls
      const [projectResponse, complianceResponse, jurisdictionsResponse, permitsResponse] = await Promise.all([
        projectAPI.getProject(projectId),
        projectAPI.getProjectComplianceChecks(projectId),
        jurisdictionAPI.getJurisdictions(),
        permitAPI.getPermits()
      ]);

      // Handle project details
      if (projectResponse.status !== 'success' || !projectResponse.data) {
        throw new Error(projectResponse.message || 'Failed to fetch project details');
      }
      setProject(projectResponse.data);

      // Handle compliance checks
      if (complianceResponse.status === 'success' && complianceResponse.data) {
        setComplianceChecks(complianceResponse.data);
      }

      // Handle jurisdiction details
      if (jurisdictionsResponse.status === 'success' && jurisdictionsResponse.data) {
        const projectJurisdiction = jurisdictionsResponse.data.find(
          j => j.jurisdictionID === projectResponse.data.jurisdictionID
        );
        setJurisdiction(projectJurisdiction || null);
      }

      // Handle permits
      if (permitsResponse.status === 'success' && permitsResponse.data) {
        const projectPermits = permitsResponse.data.filter(permit => permit.projectID === projectId);
        setPermits(projectPermits);
      }
    } catch (error) {
      console.error('Error fetching project details:', error);
      setError('Failed to fetch project details. Please try again later.');
      setComplianceError('Failed to fetch compliance checks.');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchProjectDetails();
  }, [fetchProjectDetails]);

  const handleRunComplianceCheck = async () => {
    setIsRunningCheck(true);
    setComplianceError(null);
    try {
      const response = await projectAPI.runComplianceCheck(projectId!);
      if (response.status === 'success' && response.data) {
        // Update the UI with the new compliance data
        const complianceData = response.data;
        
        // Convert the new format to match the existing ComplianceCheck type
        const formattedChecks: ComplianceCheck[] = complianceData.permit_analyses.map(analysis => {
          // Determine status based on risk level and issues
          const issues = analysis.analysis_results.analysis.issues || [];
          const hasHighIssues = issues.some((issue: any) => issue.severity === 'high');
          const hasMediumIssues = issues.some((issue: any) => issue.severity === 'medium');
          
          let status;
          if (hasHighIssues) {
            status = 'rejected';
          } else if (hasMediumIssues) {
            status = 'in_review';
          } else {
            status = 'approved';
          }

          return {
            checkID: analysis.check_id || '',
            permitID: analysis.permit_id,
            documentID: analysis.permit_id, // Use permitID since we don't have a separate documentID
            codeID: analysis.permit_id, // Use permitID since we don't have a separate codeID
            status: status,
            issues: JSON.stringify(analysis.analysis_results.analysis), // Store the entire analysis
            checkedDate: new Date(complianceData.timestamp), // Convert string to Date
            checkedByAI: true
          };
        });

        setComplianceChecks(formattedChecks);
        
        // Show success message with summary
        setSuccessMessage(
          `Compliance check completed successfully. Analyzed ${complianceData.total_documents_analyzed} documents across ${complianceData.total_permits_analyzed} permits. Overall risk level: ${complianceData.overall_risk_level}`
        );
        
        // Refresh project details to get updated statuses
        await fetchProjectDetails();
      } else {
        setComplianceError(response.message || 'Unknown error occurred');
      }
    } catch (error) {
      console.error('Error running compliance check:', error);
      setComplianceError('Failed to run compliance check');
    } finally {
      setIsRunningCheck(false);
    }
  };

  const renderComplianceIssues = useCallback((issuesStr: string) => {
    const issues = JSON.parse(issuesStr);
    return (
      <List>
        {issues.map((issue: any, index: number) => (
          <ListItem key={issue.id || index} sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', mb: 1 }}>
              <Chip
                label={issue.severity.toUpperCase()}
                color={issue.severity === 'high' ? 'error' : issue.severity === 'medium' ? 'warning' : 'success'}
                size="small"
                sx={{ mr: 1 }}
              />
              <Typography variant="subtitle2" color="text.secondary">
                {issue.category} - {issue.code_reference}
              </Typography>
            </Box>
            <Typography variant="body1">{issue.description}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Remediation: {issue.remediation}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Document: {issue.document_name} | Estimated Effort: {issue.estimated_effort}
            </Typography>
          </ListItem>
        ))}
      </List>
    );
  }, []);

  // Add a function to render compliance statistics
  const renderComplianceStats = (stats: { total_issues: number; issues_by_severity: { high: number; medium: number; low: number } }) => {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="h6">Statistics</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center', borderRadius: '16px' }}>
              <Typography variant="h4">{stats.total_issues}</Typography>
              <Typography variant="body2">Total Issues</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: theme.palette.error.light, borderRadius: '16px' }}>
              <Typography variant="h4">{stats.issues_by_severity.high}</Typography>
              <Typography variant="body2">High Severity</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: theme.palette.warning.light, borderRadius: '16px' }}>
              <Typography variant="h4">{stats.issues_by_severity.medium}</Typography>
              <Typography variant="body2">Medium Severity</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: theme.palette.success.light, borderRadius: '16px' }}>
              <Typography variant="h4">{stats.issues_by_severity.low}</Typography>
              <Typography variant="body2">Low Severity</Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const handleDeleteProject = async () => {
    if (!project || !window.confirm('Are you sure you want to delete this project?')) {
      return;
    }

    try {
      setError(null);
      const response = await projectAPI.deleteProject(project.projectID);
      
      if (response.status === 'success') {
        navigate('/dashboard');
      } else {
        throw new Error(response.message || 'Failed to delete project');
      }
    } catch (error) {
      console.error('Error deleting project:', error);
      setError('Failed to delete project. Please try again later.');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'in progress':
        return 'success';
      case 'pending':
        return 'warning';
      case 'complete':
        return 'info';
      default:
        return 'default';
    }
  };

  const getComplianceStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'compliant':
        return {
          bg: theme.palette.success.light,
          color: theme.palette.success.dark,
        };
      case 'non_compliant':
        return {
          bg: theme.palette.error.light,
          color: theme.palette.error.dark,
        };
      default:
        return {
          bg: theme.palette.warning.light,
          color: theme.palette.warning.dark,
        };
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'compliant':
        return <CheckCircleIcon sx={{ color: theme.palette.success.main }} />;
      case 'non_compliant':
        return <CancelIcon sx={{ color: theme.palette.error.main }} />;
      default:
        return <WarningIcon sx={{ color: theme.palette.warning.main }} />;
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ p: 2 }}>
            <TextField
              fullWidth
              select
              label="Permit Type"
              name="permitType"
              value={permitData.permitType}
              onChange={handleInputChange}
              required
              sx={{ mb: 2 }}
            >
              {PERMIT_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </TextField>
           
          </Box>
        );
      case 1:
        return (
          <Box sx={{ p: 2 }}>
            <Box
              {...getRootProps()}
              sx={{
                border: '2px dashed',
                borderColor: 'divider',
                borderRadius: 1,
                p: 3,
                textAlign: 'center',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                },
              }}
            >
              <input {...getInputProps()} />
              <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography variant="body1" gutterBottom>
                Drag and drop files here, or click to select files
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Supported formats: PDF, DOC, DOCX, JPG, PNG
              </Typography>
            </Box>
            {uploadError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {uploadError}
              </Alert>
            )}
            {selectedFiles.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Selected Files:
                </Typography>
                <List dense>
                  {selectedFiles.map((file, index) => (
                    <ListItem key={index}>
                      <ListItemText
                        primary={file.name}
                        secondary={`${(file.size / 1024 / 1024).toFixed(2)} MB`}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Box>
        );
      default:
        return null;
    }
  };

  const [isAnalyzingDocument, setIsAnalyzingDocument] = useState<string | null>(null);

  const [documentsWithReports, setDocumentsWithReports] = useState<Set<string>>(new Set());
  const [checkingReports, setCheckingReports] = useState<boolean>(false);

  useEffect(() => {
    const checkExistingReports = async () => {
      if (!permits || permits.length === 0) return;

      try {
        setCheckingReports(true);
        const reportsSet = new Set<string>();
        
        // Get all permit-document pairs
        const permitDocumentPairs = permits.flatMap(permit => 
          (permit.documents || []).map(doc => ({
            permitId: permit.permitID,
            documentId: doc.documentID
          }))
        );

        // Batch request all reports at once
        const responses = await Promise.allSettled(
          permitDocumentPairs.map(pair => 
            documentComplianceAPI.getDocumentComplianceReport(pair.permitId, pair.documentId)
          )
        );

        // Process all responses
        responses.forEach((response, index) => {
          if (response.status === 'fulfilled' && response.value.status === 'success' && response.value.data) {
            reportsSet.add(permitDocumentPairs[index].documentId);
          }
        });

        setDocumentsWithReports(reportsSet);
      } catch (error) {
        console.error('Error checking compliance reports:', error);
      } finally {
        setCheckingReports(false);
      }
    };

    checkExistingReports();
  }, [permits]);

  const handleRunDocumentCompliance = async (document: DocumentType, permitId: string) => {
    try {
      setIsAnalyzingDocument(document.documentID);
      setError(null);
      
      const response = await documentComplianceAPI.analyzeDocumentCompliance(permitId, document.documentID);
      
      if (response.status === 'success' && response.data) {
        // Add the document to the set of documents with reports
        setDocumentsWithReports(prev => new Set(prev).add(document.documentID));
        // Navigate to the compliance report view
        navigate(`/document-compliance/${document.documentID}`);
      } else {
        throw new Error(response.message || 'Failed to analyze document');
      }
    } catch (error) {
      console.error('Error analyzing document:', error);
      setError('Failed to analyze document. Please try again later.');
    } finally {
      setIsAnalyzingDocument(null);
    }
  };

  const [isDocumentsView, setIsDocumentsView] = useState(true);

  const [showAllDocuments, setShowAllDocuments] = useState(false);

  const totalDocuments = permits.reduce((acc, permit) => acc + (permit.documents?.length || 0), 0);

  // Add these new state variables and handlers
  const [isDocumentListDialogOpen, setIsDocumentListDialogOpen] = useState(false);
  
  const handleReuploadDocument = async (document: DocumentType) => {
    try {
      // Create a file input element
      const fileInput = window.document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = '.pdf,.doc,.docx,.jpg,.jpeg,.png';
      
      // Handle file selection
      fileInput.onchange = async (event: Event) => {
        const target = event.target as HTMLInputElement;
        if (!target.files?.length) return;
        
        const file = target.files[0];
        const formData = new FormData();
        formData.append('file', file);
        
        // Show loading state
        setIsSubmitting(true);
        setError(null);
        
        try {
          // Upload the new version
          const response = await permitAPI.updateDocument(document.permitID, document.documentID, formData);
          
          if (response.status === 'success' && response.data) {
            // Convert API response to DocumentType
            const updatedDocument: DocumentType = {
              documentID: response.data.documentID,
              permitID: response.data.permitID,
              name: response.data.name,
              type: response.data.type,
              filePath: response.data.filePath,
              uploadDate: new Date(response.data.uploadDate),
              version: response.data.version,
              size: response.data.size,
              uploadedByUserID: response.data.uploadedByUserID,
              status: response.data.status
            };
            
            // Update the documents list with the new version
            setPermits(prevPermits => 
              prevPermits.map(permit => {
                if (permit.permitID === document.permitID) {
                  return {
                    ...permit,
                    documents: permit.documents?.map(doc => 
                      doc.documentID === document.documentID
                        ? { ...updatedDocument, permitType: permit.permitType }
                        : doc
                    )
                  };
                }
                return permit;
              })
            );
            
            // Close the dialog
            setIsDocumentListDialogOpen(false);
          } else {
            throw new Error(response.message || 'Failed to reupload document');
          }
        } catch (error) {
          console.error('Error reuploading document:', error);
          setError('Failed to reupload document. Please try again.');
        } finally {
          setIsSubmitting(false);
        }
      };
      
      // Trigger file selection
      fileInput.click();
    } catch (error) {
      console.error('Error initiating reupload:', error);
      setError('Failed to initiate reupload. Please try again.');
    }
  };
  
  const handleDeleteDocument = async (document: DocumentType) => {
    try {
      // Show confirmation dialog
      if (!window.confirm('Are you sure you want to delete this document? This action cannot be undone.')) {
        return;
      }
      
      // Show loading state
      setIsSubmitting(true);
      setError(null);
      
      const response = await permitAPI.deleteDocument(document.documentID);
      
      if (response.status === 'success') {
        // Update the documents list
        setPermits(prevPermits => 
          prevPermits.map(permit => {
            if (permit.permitID === document.permitID) {
              return {
                ...permit,
                documents: permit.documents?.filter(doc => doc.documentID !== document.documentID)
              };
            }
            return permit;
          })
        );
        
        // Close the dialog if no documents left
        const remainingDocs = permits.reduce((acc, permit) => 
          acc + (permit.documents?.filter(doc => doc.documentID !== document.documentID).length || 0), 
          0
        );
        if (remainingDocs === 0) {
          setIsDocumentListDialogOpen(false);
        }
      } else {
        throw new Error(response.message || 'Failed to delete document');
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      setError('Failed to delete document. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  if (loading) {
    return <ProjectDetailsSkeleton />;
  }

  if (!project) {
    return (
      <Box sx={{ pt: '84px' }}>
        <Container maxWidth="lg">
          <Alert severity="error">Project not found</Alert>
        </Container>
      </Box>
    );
  }

  return (
      <Container maxWidth="xl">
        <Box
          component="main"
          sx={{
            pt: '64px',
            minHeight: '100vh',
            bgcolor: 'background.default',
            pb: 4,
          }}
        >
          {/* Header Section */}
          <Box sx={{ mb: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <IconButton
                  onClick={() => navigate(-1)}
                  sx={{
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                    },
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {project?.name}
                  </Typography>
                </Box>
              </Box>

            {/* Tabs Section */}
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              aria-label="project tabs"
              variant="scrollable"
              scrollButtons="auto"
              sx={{ 
                borderBottom: '1px solid',
                borderColor: 'divider',
                mt: 1,
                '& .MuiTab-root': {
                  textTransform: 'none',
                  minWidth: 120,
                  fontWeight: 500,
                  fontSize: '1rem',
                  color: 'text.secondary',
                  py: 1.5,
                  minHeight: '48px',
                  '&.Mui-selected': {
                    color: '#4169E1',
                  },
                  '& .MuiSvgIcon-root': {
                    fontSize: '1.25rem',
                    mr: 1,
                  },
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#4169E1',
                  height: 3,
                },
              }}
            >
              <Tab 
                label="Overview" 
                icon={<DescriptionIcon />} 
                iconPosition="start"
              />
              <Tab 
                label="Documents" 
                icon={<InsertDriveFileIcon />} 
                iconPosition="start"
              />
              <Tab 
                label="Compliance" 
                icon={<RuleIcon />} 
                iconPosition="start"
              />
              <Tab 
                label="Materials" 
                icon={<InventoryIcon />} 
                iconPosition="start"
              />
              <Tab 
                label="Labor"
                icon={<PersonIcon />}
                iconPosition="start"
              />
              <Tab 
                label="History" 
                icon={<EventNoteIcon />} 
                iconPosition="start"
              />
            </Tabs>
                                      </Box>

          {error && (
            <Alert 
              severity="error" 
                              sx={{
                mb: 3,
                borderRadius: '16px',
                                }}
                              >
              {error}
            </Alert>
          )}

          {/* Content Section */}
          <Box 
                          sx={{
              '& > div[role="tabpanel"]': {
                p: 0,
              }
                          }}
                        >
            <TabPanel value={activeTab} index={0}>
              <ProjectOverview 
                project={project} 
                permits={permits}
                documentsWithReports={documentsWithReports}
                isAnalyzingDocument={isAnalyzingDocument}
                handleRunDocumentCompliance={handleRunDocumentCompliance}
                handleViewComplianceReport={handleViewComplianceReport}
              />
            </TabPanel>
            <TabPanel value={activeTab} index={1}>
              <ProjectDocuments
                permits={permits}
                handleDocumentClick={handleDocumentClick}
                setIsCreatePermitDialogOpen={setIsCreatePermitDialogOpen}
                setPermits={setPermits}
              />
            </TabPanel>
            <TabPanel value={activeTab} index={2}>
              <ProjectComplianceChecks
                permits={permits}
                documentsWithReports={documentsWithReports}
                isAnalyzingDocument={isAnalyzingDocument}
                handleRunDocumentCompliance={handleRunDocumentCompliance}
                handleViewComplianceReport={handleViewComplianceReport}
              />
            </TabPanel>
            <TabPanel value={activeTab} index={3}>
              <ProjectMaterials projectId={projectId!} />
            </TabPanel>
            <TabPanel value={activeTab} index={4}>
              <Suspense fallback={<CircularProgress />}>
                <ProjectLabor projectId={projectId!} />
              </Suspense>
            </TabPanel>
            <TabPanel value={activeTab} index={5}>
              <ProjectHistory projectId={projectId!} />
            </TabPanel>
                                      </Box>

          {/* Keep all the dialogs */}
          <DocumentDialog
                    open={isDocumentDialogOpen}
                    onClose={handleDocumentDialogClose}
            document={selectedDocument}
            documentUrl={documentUrl}
            PaperProps={{
              sx: {
                position: 'fixed',
                top: '64px',
                right: '24px',
                m: 0,
                height: 'calc(100% - 80px)',
                maxHeight: 'none',
                width: '400px',
                maxWidth: '90%',
                borderRadius: '16px',
                boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
              }
            }}
                                      sx={{
              '& .MuiBackdrop-root': {
                backgroundColor: 'transparent'
              }
            }}
          />

          <CreatePermitDialog
                    open={isCreatePermitDialogOpen}
                    onClose={handleCreatePermitDialogClose}
            activeStep={activeStep}
            handleNext={handleNext}
            handleBack={handleBack}
            handleSubmit={handleSubmit}
            permitData={permitData}
            handleInputChange={handleInputChange}
            selectedFiles={selectedFiles}
            uploadError={uploadError}
            getRootProps={getRootProps}
            getInputProps={getInputProps}
            isSubmitting={isSubmitting}
            onSubmit={handleSubmit}
            PaperProps={{
              sx: {
                position: 'fixed',
                top: '64px',
                right: '24px',
                m: 0,
                height: 'calc(100% - 80px)',
                maxHeight: 'none',
                width: '500px',
                maxWidth: '90%',
                borderRadius: '16px',
                boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
              }
            }}
            sx={{
              '& .MuiBackdrop-root': {
                backgroundColor: 'transparent'
              }
            }}
          />

          {/* Other dialogs... */}
        </Box>
      </Container>
  );
});

export default ProjectDetails; 